"use client";

import { usePathname } from "next/navigation";
import { RefObject, useEffect, useState } from "react";

/** Sections the bar turns dark over: marked media, and dark-themed sections. */
export const NAV_DARK_SELECTOR = '[data-nav-dark], [data-theme="dark"]';

/**
 * How far an element and its ancestors are translated vertically right now,
 * read from their computed transforms (2D or 3D matrices).
 */
function translateYOf(el: Element): number {
  let y = 0;
  for (let node: Element | null = el; node; node = node.parentElement) {
    const m = /^matrix(3d)?\((.+)\)$/.exec(
      window.getComputedStyle(node).transform
    );
    if (m) y += parseFloat(m[2].split(",")[m[1] ? 13 : 5]) || 0;
  }
  return y;
}

/**
 * True while a dark section (data-nav-dark on a photo, video or dark band,
 * or any data-theme="dark" section) is under the bar's label row, so the
 * navbar can turn into dark glass instead of adding white. Event-driven: one
 * IntersectionObserver whose root is cropped to a 16px band around the bar's
 * centre line, so it only fires when such a section crosses that row.
 * Rebuilt on resize and on client-side navigation.
 *
 * The band sits where the bar shows. HomeNavbar slides the bar out of view
 * with translateY while scrolling down, and a rebuild in that state (a
 * phone's address bar resizing the window, a link between two pages that
 * share the navbar) would otherwise put the band above the viewport, and
 * the bar would stop turning dark until the next rebuild.
 */
export function useNavOverDark(barRef: RefObject<HTMLElement | null>): boolean {
  const pathname = usePathname();
  const [overDark, setOverDark] = useState(false);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof IntersectionObserver === "undefined") return;
    let io: IntersectionObserver | null = null;
    let timer = 0;
    const under = new Set<Element>();

    const build = () => {
      io?.disconnect();
      under.clear();
      setOverDark(false);
      const r = bar.getBoundingClientRect();
      const mid = r.top - translateYOf(bar) + r.height / 2;
      const top = Math.max(0, mid - 8);
      const bottom = Math.max(0, window.innerHeight - (mid + 8));
      io = new IntersectionObserver(
        entries => {
          entries.forEach(e =>
            e.isIntersecting ? under.add(e.target) : under.delete(e.target)
          );
          setOverDark(under.size > 0);
        },
        { rootMargin: `${-top}px 0px ${-bottom}px 0px` }
      );
      // never the bar itself or a wrapper around it
      document.querySelectorAll(NAV_DARK_SELECTOR).forEach(el => {
        if (!el.contains(bar)) io?.observe(el);
      });
    };

    build();
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(build, 200);
    };
    window.addEventListener("resize", onResize);
    return () => {
      io?.disconnect();
      window.clearTimeout(timer);
      window.removeEventListener("resize", onResize);
    };
  }, [barRef, pathname]);

  return overDark;
}
